import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import '../styles/AlertBar.css';

const AlertBar = ({ alert, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    if (onDismiss && alert) {
      onDismiss(alert.id);
    }
  }, [alert, onDismiss]);

  useEffect(() => {
    if (!alert) return;

    if (alert.autoHideDuration && alert.autoHideDuration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, alert.autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [alert, handleDismiss]);

  if (!alert || !isVisible) return null;

  const alertTypeClass = `alert-${alert.type || 'info'}`;
  const iconMap = {
    info: 'flaticon-information',
    success: 'flaticon-check-mark',
    warning: 'flaticon-warning',
    danger: 'flaticon-alert',
  };

  const icon = iconMap[alert.type] || 'flaticon-information';

  return (
    <div className={`ul-alert-bar ${alertTypeClass} ${alert.sticky ? 'sticky' : ''}`} role="alert">
      <div className="ul-alert-bar-content">
        <div className="ul-alert-bar-icon">
          <i className={icon}></i>
        </div>

        <div className="ul-alert-bar-message">
          {alert.title && <h3 className="ul-alert-bar-title">{alert.title}</h3>}
          {alert.message && <p className="ul-alert-bar-text">{alert.message}</p>}
        </div>

        <div className="ul-alert-bar-actions">
          {alert.buttonText && alert.buttonLink && (
            <Link 
              to={alert.buttonLink} 
              className={`ul-alert-bar-button ${alert.type}`}
            >
              {alert.buttonText}
            </Link>
          )}

          {alert.dismissible !== false && (
            <button 
              className="ul-alert-bar-close"
              onClick={handleDismiss}
              aria-label="Close alert"
            >
              <i className="flaticon-close"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Alert Bar Manager Component (for managing multiple alerts)
export const AlertBarManager = ({ alerts, onDismiss }) => {
  if (!alerts || alerts.length === 0) return null;

  // Sort by priority: danger > warning > success > info
  const priorityOrder = { danger: 0, warning: 1, success: 2, info: 3 };
  const sortedAlerts = [...alerts].sort(
    (a, b) => (priorityOrder[a.type] || 999) - (priorityOrder[b.type] || 999)
  );

  // Show only the highest priority alert
  const visibleAlert = sortedAlerts[0];

  return (
    <AlertBar 
      alert={visibleAlert} 
      onDismiss={onDismiss}
    />
  );
};

export default AlertBar;
